import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { 
  Github, Twitter, Linkedin, Mail, MapPin, Calendar, Link as LinkIcon, 
  BookOpen, Award, Users, ChevronRight, ExternalLink, Code, Briefcase, X, ZoomIn, ZoomOut, RotateCw, 
  GraduationCap, Layers, Cpu, Zap, ArrowRight, Download, Star, GitBranch
} from "lucide-react";
import SEOHelmet from "../SEOHelmet";

export default function BiographyPage() {
  const [activeTab, setActiveTab] = useState("about");
  const [isLoading, setIsLoading] = useState(true);
  const [typedText, setTypedText] = useState("");
  const [typingComplete, setTypingComplete] = useState(false);
  const contentRef = useRef(null);
  const hasFetched = useRef(false);
  const { name } = useParams(); // e.g., "kikawa-stanlay"
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);  
  const [showFullBio, setShowFullBio] = useState(false);
  const profileData = location.state?.profileData;
  const fallbackImage = location.state?.fallbackImage; // fallback image passed from landing
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imageRef = useRef(null);

  // Reset transformations when modal closes
  useEffect(() => {
    if (!isImageModalOpen) {
      setZoomLevel(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
    }
  }, [isImageModalOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isImageModalOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          setIsImageModalOpen(false);
          break;
        case '+':
        case '=':
          setZoomLevel(prev => Math.min(prev + 0.5, 5));
          break;
        case '-':
          setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
          break;
        case 'r':
          setRotation(prev => (prev + 90) % 360);
          break;
        case '0':
          setZoomLevel(1);
          setRotation(0);
          setPosition({ x: 0, y: 0 });
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageModalOpen]);

  // Image drag handling
  const handleMouseDown = (e) => {
    if (zoomLevel <= 1) return;
    
    setIsDragging(true);
    setStartPosition({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || zoomLevel <= 1) return;
    
    setPosition({
      x: e.clientX - startPosition.x,
      y: e.clientY - startPosition.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    if (zoomLevel <= 1) return;
    
    setIsDragging(true);
    setStartPosition({
      x: e.touches[0].clientX - position.x,
      y: e.touches[0].clientY - position.y
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging || zoomLevel <= 1) return;
    
    setPosition({
      x: e.touches[0].clientX - startPosition.x,
      y: e.touches[0].clientY - startPosition.y
    });
  };



  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchBio = async () => {
      if (profileData && Array.isArray(profileData) && profileData.length > 0) {
        setProfile(transformProfile(profileData[0], fallbackImage));
      } else {
        try {
          const response = await fetch('/api/tm/search-bio', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name }),
          });

          if (response.ok) {
            const result = await response.json();

            if (Array.isArray(result) && result.length > 0) {
              setProfile(transformProfile(result[0], fallbackImage));
            } else {
              navigate('/'); // No match found, redirect
            }
          } else {
            navigate('/'); // Request failed, redirect
          }
        } catch (error) {
          //console.error("Error fetching profile:", error);
          navigate('/'); // Error occurred, redirect
        }
      }
    };

    fetchBio();
  }, [name, profileData, fallbackImage, navigate]);

  
  
  
  const { scrollYProgress } = useScroll({
    target: contentRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.2]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.98]);

  // Transform backend data to match frontend structure
  function transformProfile(rawData, fallbackImage = null) {
    const profileData = Array.isArray(rawData) ? rawData[0] : rawData;
  
    if (!profileData || !profileData.profile) {
      return null;
    }
    return {      
      name: profileData.profile?.name || "Unknown",
      title: profileData.profile?.title || "",
      bio: profileData.profile?.bio || "",
      avatar: fallbackImage || profileData.profile?.avatar  || "https://img.favpng.com/17/24/10/computer-icons-user-profile-male-avatar-png-favpng-jhVtWQQbMdbcNCahLZztCF5wk.jpg",
      location: profileData.profile?.location || "",
      birthday: profileData.profile?.birthday || "",
      email: profileData.profile?.email || "",
      website: profileData.profile?.website || "",
      social: {
        twitter: profileData.profile?.twitter || "",
        github: profileData.profile?.github || "",
        linkedin: profileData.profile?.linkedin || ""
      },
      stats: profileData.stats || [],
      experience: profileData.experiences?.map(exp => ({
        company: exp.company,
        position: exp.position,
        period: exp.period,
        description: exp.description,
        achievements: exp.achievements || []
      })) || [],
      education: profileData.academicRecord?.map(edu => ({
        institution: edu.institution,
        degree: edu.degree,
        year: edu.year,
        thesis: edu.thesis
      })) || [],
      skills: profileData.skills?.map(skill => ({
        name: skill.name,
        level: skill.level,
        category: skill.category
      })) || [],
      projects: profileData.projects?.map(proj => ({
        name: proj.name,
        description: proj.description,
        link: proj.link,
        stars: proj.stars,
        technologies: proj.technologies || []
      })) || [],
      awards: profileData.awards?.map(award => ({
        name: award.name,
        issuer: award.issuer,
        year: award.year,
        description: award.description
      })) || []
    };
  }
  
  

  // Check if sections have data
  const hasAboutContent = profile?.bio || profile?.location || profile?.birthday || 
                         profile?.email || profile?.website;
  const hasExperience = profile?.experience?.length > 0;
  const hasEducation = profile?.education?.length > 0;
  const hasSkills = profile?.skills?.length > 0;
  const hasProjects = profile?.projects?.length > 0;
  const hasAwards = profile?.awards?.length > 0;

   // Truncate the bio if it exceeds 140 characters
   const truncatedBio = profile && profile.bio && profile.bio.length > 140 
  ? `${profile.bio.substring(0, 240)}...` 
  : profile?.bio;


  // Filter tabs based on available data
  const availableTabs = [
    { id: "about", icon: BookOpen, label: "About", visible: hasAboutContent },
    { id: "experience", icon: Briefcase, label: "Experience", visible: hasExperience },
    { id: "education", icon: GraduationCap, label: "Education", visible: hasEducation },
    { id: "skills", icon: Layers, label: "Skills", visible: hasSkills },
    { id: "projects", icon: Code, label: "Projects", visible: hasProjects },
    { id: "awards", icon: Award, label: "Awards", visible: hasAwards }
  ].filter(tab => tab.visible);



  const handleSeeMoreClick = () => {    
    // Try to get the "About" tab element and scroll to it
    const aboutTab = document.getElementById('about-tab');
    if (aboutTab) {
      aboutTab.scrollIntoView({ behavior: 'smooth' });
    } else {
      //console.error("The 'about-tab' element was not found.");
    }
  };
  
  

  // Set first available tab as active if current active is not available
  useEffect(() => {
    if (!availableTabs.some(tab => tab.id === activeTab) && availableTabs.length > 0) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  // Simulate loading and typing animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      typeWriterEffect();
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const typeWriterEffect = () => {
    const fullText = `Welcome to ${profile?.name || "my"} professional portfolio`;
    let i = 0;
    const typingInterval = setInterval(() => {
      if (i < fullText.length) {
        setTypedText(fullText.substring(0, i + 1));
        i++;
      } else {
        clearInterval(typingInterval);
        setTypingComplete(true);
      }
    }, 80);
  };

  const SkillBar = ({ name, level }) => {
    return (
      <div className="mb-4">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-gray-300">{name}</span>
          <span className="text-xs text-indigo-400">{level}%</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <motion.div 
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${level}%` }}
            transition={{ duration: 1.5, delay: 0.5 }}
          />
        </div>
      </div>
    );
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Profile not found</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHelmet
        page={`Team_${profile.name.replace(/\s/g, "_")}`}
      />
   
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950 text-gray-100">
   
      {/* Skeleton Loading Screen */}
      <AnimatePresence>
        {isLoading && (
          <motion.div 
            className="fixed inset-0 bg-gray-950 z-50 flex flex-col items-center justify-center p-4"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Skeleton Profile Card */}
            <div className="w-full max-w-4xl bg-gray-800/50 rounded-2xl shadow-2xl overflow-hidden border border-gray-700/50 flex flex-col md:flex-row">
              {/* Left Column Skeleton */}
              <div className="md:w-1/3 p-8 flex flex-col items-center border-b md:border-b-0 md:border-r border-gray-700/50">
                {/* Avatar Skeleton */}
                <div className="relative mb-6">
                  <div className="rounded-full h-40 w-40 border-4 border-gray-700 bg-gray-700 shadow-xl animate-pulse" />
                  <div className="absolute bottom-2 right-2 h-6 w-6 bg-gray-600 rounded-full border-2 border-gray-800 shadow-md" />
                </div>
                
                {/* Name Skeleton */}
                <div className="h-8 w-3/4 bg-gray-700 rounded mb-2 animate-pulse" />
                <div className="h-6 w-1/2 bg-gray-700 rounded mb-6 animate-pulse" />
                
                {/* Stats Skeleton */}
                <div className="grid grid-cols-2 gap-4 w-full mb-6">
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="bg-gray-700/50 rounded-lg p-3 text-center border border-gray-700/30">
                      <div className="h-7 w-full bg-gray-600 rounded mb-1 animate-pulse" />
                      <div className="h-4 w-3/4 mx-auto bg-gray-600 rounded animate-pulse" />
                    </div>
                  ))}
                </div>
                
                {/* Social Links Skeleton */}
                <div className="flex gap-4 mt-auto">
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="p-2 rounded-full bg-gray-700 animate-pulse">
                      <div className="h-5 w-5" />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Right Column Skeleton */}
              <div className="md:w-2/3 p-8">
                {/* Section Title Skeleton */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-5 w-5 bg-gray-700 rounded animate-pulse" />
                  <div className="h-6 w-1/3 bg-gray-700 rounded animate-pulse" />
                </div>
                
                {/* Bio Skeleton */}
                <div className="space-y-2 mb-6">
                  <div className="h-4 w-full bg-gray-700 rounded animate-pulse" />
                  <div className="h-4 w-5/6 bg-gray-700 rounded animate-pulse" />
                  <div className="h-4 w-4/5 bg-gray-700 rounded animate-pulse" />
                  <div className="h-4 w-full bg-gray-700 rounded animate-pulse" />
                </div>
                
                {/* Info Grid Skeleton */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <div className="h-4 w-4 bg-gray-700 rounded animate-pulse" />
                      <div className="h-4 w-3/4 bg-gray-700 rounded animate-pulse" />
                    </div>
                  ))}
                </div>
                
                {/* Button Skeleton */}
                <div className="flex flex-wrap gap-3 mt-6">
                  <div className="px-5 py-2.5 bg-gray-700 rounded-lg w-32 animate-pulse" />
                  <div className="px-5 py-2.5 bg-gray-700 rounded-lg w-32 animate-pulse" />
                </div>
              </div>
            </div>
            
            {/* Navigation Skeleton */}
            <div className="w-full max-w-4xl bg-gray-900/90 backdrop-blur-md border-b border-gray-800 shadow-sm mt-4">
              <div className="flex overflow-x-auto scrollbar-hide">
                {['About', 'Experience', 'Education', 'Skills', 'Projects', 'Awards'].map((tab) => (
                  <div key={tab} className="px-6 py-4 font-medium text-sm flex items-center gap-2">
                    <div className="h-4 w-4 bg-gray-700 rounded animate-pulse" />
                    <div className="h-4 w-12 bg-gray-700 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Content Area Skeleton */}
            <div className="w-full max-w-4xl bg-gray-800/50 rounded-lg p-6 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[1, 2].map((item) => (
                  <div key={item} className="bg-gray-700/50 rounded-xl p-6 border border-gray-700/50">
                    <div className="h-6 w-1/2 bg-gray-600 rounded mb-4 animate-pulse" />
                    <div className="space-y-3">
                      <div className="h-4 w-full bg-gray-600 rounded animate-pulse" />
                      <div className="h-4 w-5/6 bg-gray-600 rounded animate-pulse" />
                      <div className="h-4 w-3/4 bg-gray-600 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header/Profile Section */}
      <motion.div 
        className="relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/30 to-purple-900/30 opacity-20"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/80 via-gray-950/70 to-gray-950/90"></div>
        </div>
        
        {/* Profile container */}
        <div className="container mx-auto px-4 py-24 relative z-10">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400 mb-2">
              {typedText}
              {!typingComplete && <span className="animate-pulse">|</span>}
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              {profile.title || "Professional Profile"}
            </p>
          </motion.div>

          {/* Profile card */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="bg-gray-800/50 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden border border-gray-700/50 max-w-4xl mx-auto"
          >
            <div className="flex flex-col md:flex-row">
              {/* Avatar section */}
              <div className="md:w-1/3 p-8 flex flex-col items-center border-b md:border-b-0 md:border-r border-gray-700/50">
              <div className="relative mb-6 group" onClick={() => setIsImageModalOpen(true)}>
                <motion.div
                  className="relative overflow-hidden rounded-full h-56 w-56 border-4 border-indigo-500/50 shadow-xl" // Increased size to h-56 w-56
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.img 
                    src={profile.avatar}
                    alt={profile.name}
                    className="object-cover w-full h-full transition-opacity duration-200"
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.1 }}
                  />
                </motion.div>
              </div>
                
                <h2 className="text-2xl font-bold text-center mb-1">{profile.name}</h2>
                {profile.title && <p className="text-indigo-400 text-center mb-6">{profile.title}</p>}
                
                {/* Stats */}
                {profile.stats?.length > 0 && (
                  <div className="grid grid-cols-2 gap-4 w-full mb-6">
                    {profile.stats.map((stat, index) => (
                      <motion.div
                        key={index}
                        className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700/30"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 1.4 + index * 0.1 }}
                      >
                        <p className="text-2xl font-bold text-indigo-400">{stat.value}</p>
                        <p className="text-xs text-gray-400">{stat.label}</p>
                      </motion.div>
                    ))}
                  </div>
                )}
                
                {/* Social links */}
                {(profile.social?.twitter || profile.social?.github || profile.social?.linkedin || profile.email) && (
                  <motion.div 
                    className="flex gap-4 mt-auto"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.8 }}
                  >
                    {profile.social?.twitter && (
                      <a href={`https://twitter.com/${profile.social.twitter.replace('@', '')}`} 
                         className="p-2 rounded-full bg-gray-700 hover:bg-indigo-600 transition-colors">
                        <Twitter className="h-5 w-5" />
                      </a>
                    )}
                    {profile.social?.github && (
                      <a href={`https://github.com/${profile.social.github}`} 
                         className="p-2 rounded-full bg-gray-700 hover:bg-indigo-600 transition-colors">
                        <Github className="h-5 w-5" />
                      </a>
                    )}
                    {profile.social?.linkedin && (
                      <a href={`https://linkedin.com/in/${profile.social.linkedin}`} 
                         className="p-2 rounded-full bg-gray-700 hover:bg-indigo-600 transition-colors">
                        <Linkedin className="h-5 w-5" />
                      </a>
                    )}
                    {profile.email && (
                      <a href={`mailto:${profile.email}`} 
                         className="p-2 rounded-full bg-gray-700 hover:bg-indigo-600 transition-colors">
                        <Mail className="h-5 w-5" />
                      </a>
                    )}
                  </motion.div>
                )}
              </div>
              
              {/* Info section */}
              <div className="md:w-2/3 p-8">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                >
                   <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <BookOpen className="text-indigo-400" size={20} />
                      Professional Summary
                    </h3>
                    {profile.bio ? (
                      <p className="text-gray-300 mb-6">
                        {showFullBio ? profile.bio : truncatedBio}

                        {/* Show 'See more' if bio is truncated and not fully displayed */}
                        {!showFullBio && profile.bio.length > 240 && (
                          <span
                            className="text-indigo-500 cursor-pointer"
                            onClick={handleSeeMoreClick}
                          >
                            See more
                          </span>
                        )}
                      </p>
                    ) : (
                      <p className="text-gray-500 italic mb-6">No bio provided</p>
                    )}
                  
                  {(profile.location || profile.birthday || profile.email || profile.website) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {profile.location && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <MapPin className="text-indigo-400" size={16} />
                          {profile.location}
                        </div>
                      )}
                      {profile.birthday && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Calendar className="text-indigo-400" size={16} />
                          {profile.birthday}
                        </div>
                      )}
                      {profile.email && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Mail className="text-indigo-400" size={16} />
                          {profile.email}
                        </div>
                      )}
                      {profile.website && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <LinkIcon className="text-indigo-400" size={16} />
                          <a href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`} 
                             target="_blank" rel="noopener noreferrer"
                             className="hover:text-indigo-400">
                            {profile.website}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-3 mt-6">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg flex items-center gap-2 shadow-lg"
                    >
                      <Download size={16} />
                      Download CV
                    </motion.button>
                    {profile.email && (
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center gap-2 transition-colors"
                      >
                        <Users size={16} />
                        Contact Me
                      </motion.button>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Professional Image Preview Modal */}
      <AnimatePresence>
        {isImageModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsImageModalOpen(false)}
          >
            {/* Modal Header with controls */}
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-4 flex justify-between items-center z-10">
              <div className="flex items-center space-x-2">
                <button
                  className="p-2 rounded-full hover:bg-gray-800 text-gray-300 hover:text-white transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setZoomLevel(prev => Math.min(prev + 0.5, 5));
                  }}
                  title="Zoom In (+, =)"
                >
                  <ZoomIn size={20} />
                </button>
                <button
                  className="p-2 rounded-full hover:bg-gray-800 text-gray-300 hover:text-white transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
                  }}
                  title="Zoom Out (-)"
                >
                  <ZoomOut size={20} />
                </button>
                <button
                  className="p-2 rounded-full hover:bg-gray-800 text-gray-300 hover:text-white transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRotation(prev => (prev + 90) % 360);
                  }}
                  title="Rotate (r)"
                >
                  <RotateCw size={20} />
                </button>
                <button
                  className="p-2 rounded-full hover:bg-gray-800 text-gray-300 hover:text-white transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setZoomLevel(1);
                    setRotation(0);
                    setPosition({ x: 0, y: 0 });
                  }}
                  title="Reset (0)"
                >
                  <span className="text-sm font-medium">Reset</span>
                </button>
              </div>
              
              <button
                className="p-2 rounded-full hover:bg-gray-800 text-gray-300 hover:text-white transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsImageModalOpen(false);
                }}
                title="Close (Esc)"
              >
                <X size={24} />
              </button>
            </div>

            {/* Image Container */}
            <motion.div
              className="relative flex-1 w-full max-w-6xl flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              {!isImageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
              )}

              <motion.img
                ref={imageRef}
                src={profile.avatar}
                alt={`Enlarged ${profile.name}`}
                className={`max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl ${isImageLoaded ? 'block' : 'hidden'}`}
                style={{
                  transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                  cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                  x: position.x,
                  y: position.y
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                onLoad={() => setIsImageLoaded(true)}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleMouseUp}
                drag={zoomLevel > 1 ? 'x' : false}
                dragConstraints={imageRef}
                dragElastic={0.1}
              />
            </motion.div>

            {/* Modal Footer with image info */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-center z-10">
              <p className="text-white font-medium">{profile.name}</p>
              <p className="text-gray-300 text-sm mt-1">
                Zoom: {Math.round(zoomLevel * 100)}% • 
                Rotation: {rotation}° • 
                {zoomLevel > 1 ? ' Drag to pan' : ''}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Tabs - Only show if there are available tabs */}
      {availableTabs.length > 0 && (
        <motion.div
          className="sticky top-0 z-30 bg-gray-900/90 backdrop-blur-md border-b border-gray-800 shadow-sm"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <div className="container mx-auto px-4">
            <div className="flex overflow-x-auto scrollbar-hide">
              {availableTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 font-medium text-sm flex items-center gap-2 transition-all ${
                    activeTab === tab.id
                      ? "border-b-2 border-indigo-500 text-indigo-400"
                      : "text-gray-400 hover:text-gray-200"
                  }`}
                >
                  <tab.icon size={18} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Content Area */}
      <motion.div
        ref={contentRef}
        className="container mx-auto px-4 py-16"
      >
        <AnimatePresence mode="wait">
          {/* About Tab */}
          {activeTab === "about" && hasAboutContent && (
            <motion.div
              key="about"
              id="about-tab"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              <div className="lg:col-span-2">
                {profile.bio && (
                  <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-lg mb-8">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-indigo-400">
                      <BookOpen size={20} />
                      Professional Journey
                    </h3>
                    <div className="prose prose-invert max-w-none">
                      <p className="text-gray-300">{profile.bio}</p>
                    </div>
                  </div>
                )}

                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-indigo-400">
                    <Cpu size={20} />
                    Technical Philosophy
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      {
                        icon: Zap,
                        title: "Performance First",
                        description: "Optimizing for speed and efficiency in all applications."
                      },
                      {
                        icon: Layers,
                        title: "Modular Design",
                        description: "Building with reusable components and clear architecture."
                      },
                      {
                        icon: GitBranch,
                        title: "Iterative Development",
                        description: "Continuous improvement through small, frequent updates."
                      },
                      {
                        icon: Award,
                        title: "Quality Focus",
                        description: "Maintaining high standards through testing and reviews."
                      }
                    ].map((item, index) => (
                      <motion.div
                        key={index}
                        whileHover={{ y: -5 }}
                        className="bg-gray-800/30 p-4 rounded-lg border border-gray-700/50"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 rounded-lg bg-indigo-500/10">
                            <item.icon className="text-indigo-400" size={18} />
                          </div>
                          <h4 className="font-medium">{item.title}</h4>
                        </div>
                        <p className="text-sm text-gray-400">{item.description}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {profile.awards?.length > 0 && (
                <div>
                  <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-lg sticky top-24">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-indigo-400">
                      <Award size={20} />
                      Awards & Recognition
                    </h3>
                    <div className="space-y-4">
                      {profile.awards.map((award, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 * index }}
                          className="flex items-start gap-3"
                        >
                          <div className="mt-1 min-w-5 h-5 w-5 rounded-full bg-indigo-500/20 flex items-center justify-center">
                            <Star className="h-3 w-3 text-indigo-400" />
                          </div>
                          <div>
                            <h4 className="font-medium">{award.name}</h4>
                            {award.issuer && award.year && (
                              <p className="text-sm text-gray-400">{award.issuer} • {award.year}</p>
                            )}
                            {award.description && (
                              <p className="text-xs text-gray-500 mt-1">{award.description}</p>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Experience Tab */}
          {activeTab === "experience" && hasExperience && (
            <motion.div
              key="experience"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              {profile.experience.map((job, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-lg"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                    <div>
                      {job.position && <h3 className="text-xl font-medium">{job.position}</h3>}
                      {job.company && <p className="text-indigo-400">{job.company}</p>}
                    </div>
                    {job.period && (
                      <span className="text-sm bg-gray-700 px-3 py-1 rounded-full">{job.period}</span>
                    )}
                  </div>
                  
                  {job.description && <p className="text-gray-300 mb-4">{job.description}</p>}
                  
                  {job.achievements?.length > 0 && (
                    <>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Key Achievements:</h4>
                      <ul className="space-y-2">
                        {job.achievements.map((achievement, aIndex) => (
                          <li key={aIndex} className="flex items-start gap-2">
                            <ChevronRight className="h-4 w-4 mt-0.5 text-indigo-400 flex-shrink-0" />
                            <span className="text-gray-300">{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Education Tab */}
          {activeTab === "education" && hasEducation && (
            <motion.div
              key="education"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
            >
              {profile.education.map((edu, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-lg"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      {edu.degree && <h3 className="text-xl font-medium">{edu.degree}</h3>}
                      {edu.institution && <p className="text-indigo-400">{edu.institution}</p>}
                    </div>
                    {edu.year && (
                      <span className="text-sm bg-gray-700 px-3 py-1 rounded-full">{edu.year}</span>
                    )}
                  </div>
                  
                  {edu.thesis && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-400 mb-1">Thesis:</h4>
                      <p className="text-gray-300">{edu.thesis}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Skills Tab */}
          {activeTab === "skills" && hasSkills && (
            <motion.div
              key="skills"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8"
            >
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-lg">
                <h3 className="text-xl font-semibold mb-6 flex items-center gap-2 text-indigo-400">
                  <Layers size={20} />
                  Technical Skills
                </h3>
                <div className="space-y-4">
                  {profile.skills
                    .filter(skill => !skill.category || skill.category === 'technical')
                    .map((skill, index) => (
                      <SkillBar key={index} name={skill.name} level={skill.level} />
                    ))}
                </div>
              </div>
              
              <div className="space-y-8">
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-indigo-400">
                    <Zap size={20} />
                    Expertise Areas
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.skills
                      .filter(skill => skill.category === 'expertise')
                      .map((skill, index) => (
                        <SkillBar key={index} name={skill.name} level={skill.level} />
                      ))}
                  </div>
                </div>
                
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-indigo-400">
                    <Briefcase size={20} />
                    Professional Skills
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {profile.skills
                      .filter(skill => skill.category === 'professional')
                      .map((skill, index) => (
                        <motion.span
                          key={index}
                          whileHover={{ scale: 1.05 }}
                          className="px-3 py-1.5 bg-gray-700/50 text-gray-300 rounded-full text-sm"
                        >
                          {skill.name}
                        </motion.span>
                      ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Projects Tab */}
          {activeTab === "projects" && hasProjects && (
            <motion.div
              key="projects"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {profile.projects.map((project, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-xl overflow-hidden border border-gray-700/50 shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="h-48 bg-gradient-to-br from-indigo-900/50 to-purple-900/50 relative overflow-hidden">
                    <div className="absolute inset-0 bg-indigo-900/30 opacity-70"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-gray-900/90 to-transparent">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium">{project.name}</h3>
                        {project.stars && (
                          <div className="flex items-center gap-1 text-sm text-gray-400">
                            <Star className="h-4 w-4 text-yellow-400" />
                            {project.stars.toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    {project.description && (
                      <p className="text-gray-300 text-sm mb-4">{project.description}</p>
                    )}
                    
                    {project.technologies?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.technologies.map((tech, tIndex) => (
                          <span key={tIndex} className="px-2 py-1 bg-indigo-500/10 text-indigo-400 rounded-full text-xs">
                            {tech}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {project.link && (
                      <motion.a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className="inline-flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300"
                      >
                        View Project <ExternalLink className="h-4 w-4" />
                      </motion.a>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Awards Tab */}
          {activeTab === "awards" && hasAwards && (
            <motion.div
              key="awards"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {profile.awards.map((award, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-lg"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-indigo-500/10">
                      <Award className="h-6 w-6 text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-medium">{award.name}</h3>
                      {award.issuer && (
                        <p className="text-sm text-gray-400">{award.issuer}</p>
                      )}
                    </div>
                  </div>
                  {award.description && (
                    <p className="text-gray-300 text-sm mb-4">{award.description}</p>
                  )}
                  {award.year && (
                    <div className="text-xs text-gray-500">{award.year}</div>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Footer */}
      <footer className="bg-gray-900/80 border-t border-gray-800 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
                <Code className="h-4 w-4 text-white" />
              </div>
              <span className="font-medium">{profile.name}</span>
            </div>
            
            <div className="flex gap-4">
              {profile.social?.twitter && (
                <a href={`https://twitter.com/${profile.social.twitter.replace('@', '')}`} 
                   className="text-gray-400 hover:text-indigo-400 transition-colors">
                  <Twitter size={18} />
                </a>
              )}
              {profile.social?.github && (
                <a href={`https://github.com/${profile.social.github}`} 
                   className="text-gray-400 hover:text-indigo-400 transition-colors">
                  <Github size={18} />
                </a>
              )}
              {profile.social?.linkedin && (
                <a href={`https://linkedin.com/in/${profile.social.linkedin}`} 
                   className="text-gray-400 hover:text-indigo-400 transition-colors">
                  <Linkedin size={18} />
                </a>
              )}
              {profile.email && (
                <a href={`mailto:${profile.email}`} 
                   className="text-gray-400 hover:text-indigo-400 transition-colors">
                  <Mail size={18} />
                </a>
              )}
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-800/50 text-center text-sm text-gray-500">
            <p>© {new Date().getFullYear()} All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}